import { useQuery } from 'react-query';
import { oktaGet, oktaPost } from '../oktaRequestWrapper';
import { ApiRoutes } from '../apiRoutes';
import {
  DestinationChange,
  DestinationResponse,
  SchedulePayload,
  ScheduleResponse,
} from '@/app/features/DebtorPortal/CommunicationPreferences/communicationPreferences.types';

const COMM_PREF_API_VERSION = '2.0';
const requestConfig = { headers: { 'api-version': COMM_PREF_API_VERSION } };

export const scheduleQueryKey = ['communication-preferences', 'schedule'];
export const destinationsQueryKey = ['communication-preferences', 'destinations'];

export const useSchedule = (enabled = true) =>
  useQuery({
    queryKey: scheduleQueryKey,
    enabled,
    queryFn: async (): Promise<ScheduleResponse> => {
      const response = await oktaGet<ScheduleResponse>(
        ApiRoutes.CommunicationPreferencesSchedule,
        requestConfig,
      );
      return response.data;
    },
  });

export const useDestinations = (enabled = true) =>
  useQuery({
    queryKey: destinationsQueryKey,
    enabled,
    queryFn: async (): Promise<DestinationResponse[]> => {
      const response = await oktaGet<DestinationResponse[]>(
        ApiRoutes.CommunicationPreferencesDestinations,
        requestConfig,
      );
      return response.data ?? [];
    },
  });

export const saveSchedule = async (
  payload: SchedulePayload,
): Promise<ScheduleResponse> => {
  const response = await oktaPost<ScheduleResponse>(
    ApiRoutes.CommunicationPreferencesSchedule,
    payload,
    requestConfig,
  );
  return response.data;
};

export const saveDestinations = async (
  changes: DestinationChange[],
): Promise<DestinationResponse[]> => {
  const response = await oktaPost<DestinationResponse[]>(
    ApiRoutes.CommunicationPreferencesDestinations,
    changes,
    requestConfig,
  );
  return response.data ?? [];
};

/**
 * Resolves a user-facing message from either 400 shape (RFC 7807
 * ValidationProblemDetails JSON or a plain-text business-rule message) and 500s.
 */
export const parseApiError = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data) return fallback;

  if (typeof data === 'string') {
    return data.trim() || fallback;
  }

  const problem = data as {
    errors?: Record<string, string[] | string>;
    title?: string;
    detail?: string;
  };

  if (problem.errors && typeof problem.errors === 'object') {
    const firstKey = Object.keys(problem.errors)[0];
    const firstValue = firstKey ? problem.errors[firstKey] : undefined;
    if (Array.isArray(firstValue) && firstValue.length > 0) return firstValue[0];
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }

  if (typeof problem.detail === 'string' && problem.detail.trim()) return problem.detail;
  if (typeof problem.title === 'string' && problem.title.trim()) return problem.title;

  return fallback;
};
